import { ContainerTask, StatusConfig, TaskTypeConfig, DriverData } from '../types/container'

// ============================================
// Order Status 配置 (5 个状态，按照 STATUS_DESIGN.md v2.1 规范)
// ============================================
const _orderStatusConfig: Record<string, StatusConfig> = {
  "pending": {
    label: "Pending",
    bgColor: "bg-yellow-50",
    textColor: "text-yellow-700",
    borderColor: "border-yellow-200",
  },
  "in-progress": {
    label: "In Progress",
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    borderColor: "border-blue-200",
  },
  "paused": {
    label: "Paused",
    bgColor: "bg-orange-50",
    textColor: "text-orange-700",
    borderColor: "border-orange-200",
  },
  "completed": {
    label: "Completed",
    bgColor: "bg-green-50",
    textColor: "text-green-700",
    borderColor: "border-green-200",
  },
  "cancelled": {
    label: "Cancelled",
    bgColor: "bg-red-50",
    textColor: "text-red-700",
    borderColor: "border-red-200",
  },
}

// ============================================
// Task Status 配置 (16 个状态，按照 STATUS_DESIGN.md v3.0 规范)
// ============================================
export const taskStatusConfig: Record<string, StatusConfig> = {
  "on-vessel": {
    label: "On Vessel",
    bgColor: "bg-purple-50",
    textColor: "text-purple-700",
    borderColor: "border-purple-200",
  },
  "released": {
    label: "Released",
    bgColor: "bg-green-50",
    textColor: "text-green-700",
    borderColor: "border-green-200",
  },
  "on-hold": {
    label: "On Hold",
    bgColor: "bg-orange-50",
    textColor: "text-orange-700",
    borderColor: "border-orange-200",
  },
  "unassigned": {
    label: "Unassigned",
    bgColor: "bg-amber-50",
    textColor: "text-amber-700",
    borderColor: "border-amber-200",
  },
  "assigned": {
    label: "Assigned",
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    borderColor: "border-blue-200",
  },
  "picking": {
    label: "Picking",
    bgColor: "bg-sky-50",
    textColor: "text-sky-700",
    borderColor: "border-sky-200",
  },
  "delivering": {
    label: "Delivering",
    bgColor: "bg-indigo-50",
    textColor: "text-indigo-700",
    borderColor: "border-indigo-200",
  },
  "on-stop": {
    label: "On Stop",
    bgColor: "bg-yellow-50",
    textColor: "text-yellow-700",
    borderColor: "border-yellow-200",
  },
  "completed": {
    label: "Completed",
    bgColor: "bg-emerald-50",
    textColor: "text-emerald-700",
    borderColor: "border-emerald-200",
  },
  "loading": {
    label: "Loading",
    bgColor: "bg-cyan-50",
    textColor: "text-cyan-700",
    borderColor: "border-cyan-200",
  },
  "empty": {
    label: "Empty",
    bgColor: "bg-gray-50",
    textColor: "text-gray-700",
    borderColor: "border-gray-200",
  },
  "loaded": {
    label: "Loaded",
    bgColor: "bg-lime-50",
    textColor: "text-lime-700",
    borderColor: "border-lime-200",
  },
  "returning": {
    label: "Returning",
    bgColor: "bg-teal-50",
    textColor: "text-teal-700",
    borderColor: "border-teal-200",
  },
  "cancelled": {
    label: "Cancelled",
    bgColor: "bg-red-50",
    textColor: "text-red-700",
    borderColor: "border-red-200",
  },
  "paused": {
    label: "Paused",
    bgColor: "bg-rose-50",
    textColor: "text-rose-700",
    borderColor: "border-rose-200",
  },
  "yard-stored": {
    label: "Yard Stored",
    bgColor: "bg-violet-50",
    textColor: "text-violet-700",
    borderColor: "border-violet-200",
  },
  "pcc-hold": {
    label: "CBP Hold",
    bgColor: "bg-amber-50",
    textColor: "text-amber-700",
    borderColor: "border-amber-200",
  },
  "uncompleted": {
    label: "Uncompleted",
    bgColor: "bg-stone-50",
    textColor: "text-stone-700",
    borderColor: "border-stone-200",
  },
}

// 完整状态配置 (包含所有状态类型，用于显示状态标签等)
export const statusConfig: Record<string, StatusConfig> = {
  // 任务状态 (16个)
  ...taskStatusConfig,
  // Driver statuses (4个，按照 STATUS_DESIGN.md)
  "on-duty": {
    label: "On Duty",
    bgColor: "bg-green-100",
    textColor: "text-green-700",
    borderColor: "border-green-200",
  },
  "on-task": {
    label: "On Task",
    bgColor: "bg-yellow-100",
    textColor: "text-yellow-700",
    borderColor: "border-yellow-200",
  },
  "off-duty": {
    label: "Off Duty",
    bgColor: "bg-gray-100",
    textColor: "text-gray-700",
    borderColor: "border-gray-200",
  },
  "on-leave": {
    label: "On Leave",
    bgColor: "bg-red-100",
    textColor: "text-red-700",
    borderColor: "border-red-200",
  },
  // Truck statuses (4个，按照 STATUS_DESIGN.md)
  "available": {
    label: "Available",
    bgColor: "bg-green-100",
    textColor: "text-green-700",
    borderColor: "border-green-200",
  },
  "in-use": {
    label: "In Use",
    bgColor: "bg-purple-100",
    textColor: "text-purple-700",
    borderColor: "border-purple-200",
  },
  "assigned": {
    label: "Assigned",
    bgColor: "bg-blue-100",
    textColor: "text-blue-700",
    borderColor: "border-blue-200",
  },
  "maintenance": {
    label: "Maintenance",
    bgColor: "bg-orange-100",
    textColor: "text-orange-700",
    borderColor: "border-orange-200",
  },
  "out-of-service": {
    label: "Out of Service",
    bgColor: "bg-red-100",
    textColor: "text-red-700",
    borderColor: "border-red-200",
  },
  // Chassis status "retired" (not in task/truck/driver)
  "retired": {
    label: "Retired",
    bgColor: "bg-slate-100",
    textColor: "text-slate-700",
    borderColor: "border-slate-200",
  },
}

// 任务类型配置 (6 种类型，按照 STATUS_DESIGN.md v3.0 规范)
export const taskTypeConfig: Record<string, TaskTypeConfig> = {
  "load-out": {
    label: "Load Out",
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
  },
  "load-in": {
    label: "Load In",
    bgColor: "bg-green-50",
    textColor: "text-green-700",
  },
  "empty-in": {
    label: "Empty In",
    bgColor: "bg-yellow-50",
    textColor: "text-yellow-700",
  },
  "empty-out": {
    label: "Empty Out",
    bgColor: "bg-orange-50",
    textColor: "text-orange-700",
  },
  "delivery": {
    label: "Delivery",
    bgColor: "bg-violet-50",
    textColor: "text-violet-700",
  },
  "return": {
    label: "Return",
    bgColor: "bg-teal-50",
    textColor: "text-teal-700",
  },
}

// Task Subtype 配置 (Task Action: Drop vs Live-Load)
export const taskSubtypeConfig: Record<string, { label: string; description: string }> = {
  "drop": {
    label: "Drop",
    description: "Drop container at destination and leave",
  },
  "live-load": {
    label: "Live Load",
    description: "Wait at destination for loading/unloading",
  },
}

// 司机列表
const _drivers = [
  "张师傅",
  "李师傅",
  "王师傅",
  "陈师傅",
  "刘师傅",
  "赵师傅",
  "孙师傅",
  "周师傅",
  "吴师傅",
  "郑师傅",
  "钱师傅",
  "冯师傅",
]

// 详细司机数据
const _mockAvailableDrivers: DriverData[] = [
  {
    id: "driver-1",
    name: "张师傅",
    avatar: "/placeholder.svg?height=32&width=32",
    status: "Available",
    location: "Terminal 3",
    rating: 5,
    currentTasks: [
      { id: "T001", containerNumber: "MSKU7654321" },
      { id: "T005", containerNumber: "HLXU7890123" },
    ],
    phone: "+1 (555) 123-4567",
    email: "zhang.driver@example.com",
    company: "Golden Logistics",
    plateLicense: "CA-1234567",
  },
  {
    id: "driver-2",
    name: "李师傅",
    avatar: "/placeholder.svg?height=32&width=32",
    status: "Busy until 3:00 PM",
    location: "Oakland Port",
    rating: 4,
    currentTasks: [
      { id: "T002", containerNumber: "COSCO9876543" },
      { id: "T014", containerNumber: "COSCO4567890" },
      { id: "T015", containerNumber: "ONEU1234567" },
    ],
    phone: "+1 (555) 234-5678",
    email: "li.driver@example.com",
    company: "Swift Transport",
    plateLicense: "CA-2345678",
  },
  {
    id: "driver-3",
    name: "王师傅",
    avatar: "/placeholder.svg?height=32&width=32",
    status: "Available",
    location: "Depot A",
    rating: 5,
    currentTasks: [],
    phone: "+1 (555) 345-6789",
    email: "wang.driver@example.com",
    company: "Reliable Freight",
    plateLicense: "CA-3456789",
  },
  {
    id: "driver-4",
    name: "陈师傅",
    avatar: "/placeholder.svg?height=32&width=32",
    status: "On Break",
    location: "Rest Stop",
    rating: 3,
    currentTasks: [{ id: "T004", containerNumber: "EGHU4567890" }],
    phone: "+1 (555) 456-7890",
    email: "chen.driver@example.com",
    company: "陈师傅",
    plateLicense: "CA-4567890",
  },
]

// 目的地选项
const _destinations = [
  "Oakland, CA",
  "Long Beach, CA",
  "Los Angeles, CA",
  "Seattle, WA",
  "Tacoma, WA",
  "Shanghai, China",
  "Busan, South Korea",
  "Tokyo, Japan",
  "Hamburg, Germany",
  "Singapore",
]

// 进出口选项
export const importExportOptions = [
  { value: "all", label: "All" },
  { value: "import", label: "Import" },
  { value: "export", label: "Export" },
]

// 调度过滤选项
const _scheduleFilterOptions = [
  { value: "all", label: "All Schedules" },
  { value: "today", label: "Today" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "this-week", label: "This Week" },
  { value: "overdue", label: "Overdue" },
]

// 货主列表 (从数据中提取)
const _shippers = [
  "ABC Manufacturing Corp",
  "DEF Electronics Ltd",
  "GHI Automotive Inc",
  "JKL Trading Co",
  "MNO Textiles Ltd",
  "PQR Food Products",
  "STU Chemical Corp",
  "VWX Machinery Ltd",
  "YZA Retail Group",
  "BCD Electronics",
  "EFG Furniture Co",
  "HIJ Pharmaceuticals",
  "Global Imports Inc.",
  "Export Goods Co.",
  "Import Goods Inc.",
]

// 增强的模拟容器数据 (包含20个完整数据)
const _mockContainerData: ContainerTask[] = [
  {
    id: "1",
    taskId: "T001",
    containerNumber: "MSKU7654321",
    shippingLine: "Maersk",
    shippingLineCode: "MSK",
    taskType: "load-out",
    taskSubtype: "live-load",
    status: "released",
    driver: "张师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-16T14:00:00",
    lfd: "2024-01-18T17:00:00",
    shipper: "ABC Manufacturing Corp",
    shipperContact: { phone: "555-123-4567", email: "contact@abc.com" },
    warehouse: "XYZ Distribution Center",
    warehouseAddress: "1234 Industrial Blvd, Los Angeles, CA",
    warehouseContact: { phone: "555-987-6543", email: "xyz@storage.com" },
    warehouseAppointment: "2024-01-17T09:00:00",
    internalNote: "Priority shipment - handle with care. Customer requested expedited processing.",
    containerSize: "40HC",
    containerType: "dry",
    chassisNumber: "CH-12345",
    weight: 42000,
    importExport: "export",
    originPort: "Long Beach, CA",
    destinationPort: "Oakland, CA",
    bookingReference: "BK789012",
    vesselInformation: "MSK VESSEL / Voyage 001",
    specialInstructions: "Handle with care",
    holdReasons: [],
    portContacts: {
      ssa: { ref: "SSA123456", phone: "562-123-4567", email: "ssa@port.com" },
    },
    gateCode: "TRACY123",
    pin: "4567",
    ssaBooking: "SSA789012",
    sealNumber: "SL123456",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "Terminal 5",
    availableTimes: "9AM-12PM, 2PM-5PM",
    dispatcherToDriverInstructions: "Call warehouse before delivery; Take photos of any damage",
    processingTimeline: [
      { time: "09:00 AM", description: "Container released by port", status: "completed" },
      { time: "02:30 PM", description: "Driver assigned and notified", status: "completed" },
      { time: "03:45 PM", description: "En route to terminal", status: "current" },
      { time: "04:30 PM", description: "Estimated pickup completion", status: "pending" },
      { time: "06:00 PM", description: "Estimated delivery to warehouse", status: "pending" },
    ],
    nextActions: ["Driver ETA to terminal: 15 minutes", "Prepare delivery documentation"],
    lastUpdate: "15 minutes ago",
    driverStatus: "En route to pickup",
    driverContactStatus: "Available",
    driverLastSeen: "2 mins ago",
    portName: "Port of Long Beach",
  },
  {
    id: "2",
    taskId: "T002",
    containerNumber: "COSCO9876543",
    shippingLine: "COSCO",
    shippingLineCode: "COSCO",
    taskType: "load-in",
    taskSubtype: "drop",
    status: "on-hold",
    driver: "李师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "Pending",
    lfd: "2024-01-17T16:00:00",
    shipper: "DEF Electronics Ltd",
    shipperContact: { phone: "555-222-3333", email: "def@electronics.com" },
    warehouse: "ABC Storage Facility",
    warehouseAddress: "5678 Commerce St, Long Beach, CA",
    warehouseContact: { phone: "555-444-5555", email: "abc@storage.com" },
    warehouseAppointment: "2024-01-16T15:00:00",
    internalNote: "Waiting for customs clearance. Documentation pending review.",
    containerSize: "20GP",
    containerType: "reefer",
    chassisNumber: "CH-67890",
    importExport: "import",
    originPort: "Shanghai, China",
    destinationPort: "Long Beach, CA",
    bookingReference: "BK345678",
    vesselInformation: "COSCO SHIP / Voyage 002",
    specialInstructions: "Customs hold, check documents",
    holdReasons: ["Customs Hold", "Documentation Pending"],
    holdReasonDetail: "Missing documentation",
    portContacts: {
      trapac: { ref: "TRP789012", phone: "562-234-5678", email: "trapac@port.com" },
    },
    gateCode: "COSCO987",
    pin: "1234",
    ssaBooking: "COSCOBOOK",
    sealNumber: "SL987654",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "Customs Office",
    availableTimes: "N/A",
    dispatcherToDriverInstructions: "Await customs clearance before proceeding.",
    processingTimeline: [
      { time: "08:00 AM", description: "Container arrived at port", status: "completed" },
      { time: "09:00 AM", description: "Customs hold initiated", status: "current" },
      { time: "Pending", description: "Customs clearance", status: "pending" },
      { time: "Pending", description: "Driver assignment", status: "pending" },
    ],
    nextActions: ["Follow up with customs", "Notify shipper of delay"],
    lastUpdate: "3 hours ago",
    driverStatus: "Waiting for clearance",
    driverContactStatus: "Unavailable",
    driverLastSeen: "1 hour ago",
    portName: "Port of Los Angeles",
  },
  {
    id: "3",
    taskId: "T003",
    containerNumber: "OOLU1234567",
    shippingLine: "OOCL",
    shippingLineCode: "OOCL",
    taskType: "empty-in",
    taskSubtype: "drop",
    status: "completed",
    driver: "王师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-14T11:00:00",
    lfd: "2024-01-16T18:00:00",
    shipper: "GHI Automotive Inc",
    shipperContact: { phone: "555-777-8888", email: "ghi@auto.com" },
    warehouse: "DEF Logistics Hub",
    warehouseAddress: "9012 Freight Ave, Carson, CA",
    warehouseContact: { phone: "555-999-0000", email: "def@logistics.com" },
    warehouseAppointment: "2024-01-15T10:00:00",
    internalNote: "Successfully completed. No issues reported during transport.",
    containerSize: "40GP",
    containerType: "dry",
    chassisNumber: "CH-54321",
    importExport: "n/a",
    originPort: "Los Angeles, CA",
    destinationPort: "Los Angeles, CA",
    bookingReference: "BK901234",
    vesselInformation: "OOCL STAR / Voyage 003",
    specialInstructions: "None",
    holdReasons: [],
    portContacts: {
      ets: { ref: "ETS345678", phone: "562-345-6789", email: "ets@port.com" },
    },
    gateCode: "OOCLDEPOT",
    pin: "5678",
    ssaBooking: "OOCLBOOK",
    sealNumber: "SL112233",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "Empty Depot",
    availableTimes: "Anytime",
    dispatcherToDriverInstructions: "Return empty to depot.",
    processingTimeline: [
      { time: "08:00 AM", description: "Container picked up", status: "completed" },
      { time: "09:30 AM", description: "En route to depot", status: "completed" },
      { time: "10:00 AM", description: "Container returned to depot", status: "completed" },
    ],
    nextActions: ["Archive task"],
    lastUpdate: "2 days ago",
    driverStatus: "Completed task",
    driverContactStatus: "Available",
    driverLastSeen: "2 days ago",
    portName: "Port of Long Beach",
  },
  {
    id: "4",
    taskId: "T004",
    containerNumber: "EGHU4567890",
    shippingLine: "Evergreen",
    shippingLineCode: "EMC",
    taskType: "empty-out",
    taskSubtype: "live-load",
    status: "picking",
    driver: "陈师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-13T10:00:00",
    lfd: "2024-01-20T17:00:00",
    shipper: "JKL Trading Co",
    shipperContact: { phone: "555-111-2222", email: "jkl@trading.com" },
    warehouse: "GHI Container Yard",
    warehouseAddress: "3456 Terminal Rd, San Pedro, CA",
    warehouseContact: { phone: "555-333-4444", email: "ghi@yard.com" },
    warehouseAppointment: "2024-01-14T13:00:00",
    internalNote: "Empty container return. Inspect for damage before drop-off.",
    containerSize: "20GP",
    containerType: "dry",
    chassisNumber: "CH-98765",
    importExport: "export",
    originPort: "Los Angeles, CA",
    destinationPort: "Seattle, WA",
    bookingReference: "BK567890",
    vesselInformation: "EVERGREEN SUN / Voyage 004",
    specialInstructions: "Inspect for damage",
    holdReasons: [],
    portContacts: {
      ssa: { ref: "SSA456789", phone: "562-123-4567", email: "ssa@port.com" },
    },
    gateCode: "EVERGREEN",
    pin: "9012",
    ssaBooking: "EMCBOOK",
    sealNumber: "SL445566",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "Shipper's Yard",
    availableTimes: "8AM-4PM",
    dispatcherToDriverInstructions: "Ensure container is empty and clean.",
    processingTimeline: [
      { time: "09:00 AM", description: "Driver dispatched", status: "completed" },
      { time: "10:30 AM", description: "Arrived at shipper's yard", status: "current" },
      { time: "11:00 AM", description: "Estimated pickup completion", status: "pending" },
    ],
    nextActions: ["Confirm empty container pickup"],
    lastUpdate: "30 minutes ago",
    driverStatus: "At shipper's yard",
    driverContactStatus: "Available",
    driverLastSeen: "5 mins ago",
  },
  {
    id: "5",
    taskId: "T005",
    containerNumber: "HLXU7890123",
    shippingLine: "Hapag-Lloyd",
    shippingLineCode: "HPL",
    taskType: "load-in",
    taskSubtype: "drop",
    status: "delivering",
    driver: "刘师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-12T15:00:00",
    lfd: "2024-01-25T16:00:00",
    shipper: "MNO Textiles Ltd",
    shipperContact: { phone: "555-555-6666", email: "mno@textiles.com" },
    warehouse: "JKL Distribution",
    warehouseAddress: "7890 Warehouse Way, Torrance, CA",
    warehouseContact: { phone: "555-777-8888", email: "jkl@distro.com" },
    warehouseAppointment: "2024-01-13T08:00:00",
    internalNote: "Temperature-controlled cargo. Maintain cold chain throughout transport.",
    containerSize: "40HC",
    containerType: "reefer",
    chassisNumber: "CH-11111",
    importExport: "import",
    originPort: "Busan, South Korea",
    destinationPort: "Los Angeles, CA",
    bookingReference: "BK112233",
    vesselInformation: "HAPAG EXPRESS / Voyage 005",
    specialInstructions: "Maintain cold chain",
    holdReasons: [],
    portContacts: {
      trapac: { ref: "TRP901234", phone: "562-234-5678", email: "trapac@port.com" },
    },
    gateCode: "HAPAG",
    pin: "3456",
    ssaBooking: "HPLBOOK",
    sealNumber: "SL778899",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "En Route to Warehouse",
    availableTimes: "8AM-12PM",
    dispatcherToDriverInstructions: "Monitor reefer temperature.",
    processingTimeline: [
      { time: "07:00 AM", description: "Departed port", status: "completed" },
      { time: "07:30 AM", description: "En route to warehouse", status: "current" },
      { time: "08:00 AM", description: "Estimated arrival at warehouse", status: "pending" },
    ],
    nextActions: ["Prepare for delivery at warehouse"],
    lastUpdate: "10 minutes ago",
    driverStatus: "En route to delivery",
    driverContactStatus: "Available",
    driverLastSeen: "1 min ago",
    portName: "TraPac Terminal",
  },
  {
    id: "6",
    taskId: "T006",
    containerNumber: "CMAU5555666",
    shippingLine: "CMA CGM",
    shippingLineCode: "CMA",
    taskType: "load-out",
    taskSubtype: "live-load",
    status: "vessel",
    driver: "赵师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-20T13:00:00",
    lfd: "2024-01-22T17:00:00",
    shipper: "PQR Food Products",
    shipperContact: { phone: "555-888-9999", email: "pqr@food.com" },
    warehouse: "MNO Cold Storage",
    warehouseAddress: "2468 Refrigeration Blvd, Vernon, CA",
    warehouseContact: { phone: "555-000-1111", email: "mno@coldstorage.com" },
    warehouseAppointment: "2024-01-21T09:00:00",
    internalNote: "Container on vessel, arriving tomorrow. Prepare for pickup.",
    containerSize: "20GP",
    containerType: "reefer",
    chassisNumber: "CH-22222",
    importExport: "export",
    originPort: "Long Beach, CA",
    destinationPort: "Tacoma, WA",
    bookingReference: "BK445566",
    vesselInformation: "CMA CGM TITAN / Voyage 006",
    specialInstructions: "Vessel arrival Jan 20",
    holdReasons: [],
    portContacts: {
      ets: { ref: "ETS678901", phone: "562-345-6789", email: "ets@port.com" },
    },
    gateCode: "CMACGM",
    pin: "7890",
    ssaBooking: "CMABOOK",
    sealNumber: "SL001122",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "On Vessel",
    availableTimes: "N/A",
    dispatcherToDriverInstructions: "Monitor vessel arrival.",
    processingTimeline: [
      { time: "Jan 18", description: "Loaded on vessel", status: "completed" },
      { time: "Jan 19", description: "Vessel en route", status: "current" },
      { time: "Jan 20", description: "Estimated vessel arrival", status: "pending" },
    ],
    nextActions: ["Monitor vessel ETA", "Prepare port appointment"],
    lastUpdate: "6 hours ago",
    driverStatus: "N/A (on vessel)",
    driverContactStatus: "N/A",
    driverLastSeen: "N/A",
    portName: "Everport Terminal",
  },
  {
    id: "7",
    taskId: "T007",
    containerNumber: "YNIU1111222",
    shippingLine: "Yang Ming",
    shippingLineCode: "YML",
    taskType: "load-in",
    taskSubtype: "drop",
    status: "on-stop-yard",
    driver: "孙师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-17T16:00:00",
    lfd: "2024-01-21T18:00:00",
    shipper: "STU Chemical Corp",
    shipperContact: { phone: "555-123-9876", email: "stu@chemical.com" },
    warehouse: "PQR Hazmat Facility",
    warehouseAddress: "1357 Safety Dr, Wilmington, CA",
    warehouseContact: { phone: "555-456-7890", email: "pqr@hazmat.com" },
    warehouseAppointment: "2024-01-18T14:00:00",
    internalNote: "Hazardous materials - requires special handling certification.",
    containerSize: "40GP",
    containerType: "dry",
    chassisNumber: "CH-33333",
    importExport: "n/a",
    originPort: "Oakland, CA",
    destinationPort: "Oakland, CA",
    bookingReference: "BK778899",
    vesselInformation: "YANG MING GLORY / Voyage 007",
    specialInstructions: "Hazmat certified driver only",
    holdReasons: [],
    portContacts: {
      ssa: { ref: "SSA789012", phone: "562-123-4567", email: "ssa@port.com" },
    },
    gateCode: "YANGMING",
    pin: "1122",
    ssaBooking: "YMLBOOK",
    sealNumber: "SL334455",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "Yard",
    availableTimes: "1PM-5PM",
    dispatcherToDriverInstructions: "Verify hazmat placards.",
    processingTimeline: [
      { time: "10:00 AM", description: "Arrived at yard", status: "completed" },
      { time: "10:15 AM", description: "Waiting for gate entry", status: "current" },
      { time: "11:00 AM", description: "Estimated gate entry", status: "pending" },
    ],
    nextActions: ["Monitor yard queue", "Confirm gate entry"],
    lastUpdate: "5 minutes ago",
    driverStatus: "At yard, waiting",
    driverContactStatus: "Available",
    driverLastSeen: "1 min ago",
  },
  {
    id: "8",
    taskId: "T008",
    containerNumber: "MSKU3334444",
    shippingLine: "Maersk",
    shippingLineCode: "MSK",
    taskType: "load-in",
    taskSubtype: "drop",
    status: "loading",
    driver: "周师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-17T11:00:00",
    lfd: "2024-01-22T17:00:00",
    shipper: "VWX Machinery Ltd",
    shipperContact: { phone: "555-234-5678", email: "vwx@machinery.com" },
    warehouse: "STU Heavy Equipment",
    warehouseAddress: "8024 Industrial Park, Compton, CA",
    warehouseContact: { phone: "555-789-0123", email: "stu@heavy.com" },
    warehouseAppointment: "2024-01-17T11:00:00",
    internalNote: "Heavy machinery loading in progress. Monitor crane operations.",
    containerSize: "45HC", 
    containerType: "dry",
    chassisNumber: "CH-44444",
    importExport: "import",
    originPort: "Tokyo, Japan",
    destinationPort: "Long Beach, CA",
    bookingReference: "BK001122",
    vesselInformation: "MAERSK LINE / Voyage 008",
    specialInstructions: "Heavy machinery - use proper rigging",
    holdReasons: [],
    portContacts: {
      trapac: { ref: "TRP123456", phone: "562-234-5678", email: "trapac@port.com" },
    },
    gateCode: "MAERSK",
    pin: "4321",
    ssaBooking: "MSKBOOK",
    sealNumber: "SL667788",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "Warehouse Dock",
    availableTimes: "9AM-5PM",
    dispatcherToDriverInstructions: "Wait for loading completion.",
    processingTimeline: [
      { time: "09:00 AM", description: "Arrived at warehouse", status: "completed" },
      { time: "09:30 AM", description: "Loading started", status: "current" },
      { time: "11:00 AM", description: "Estimated loading completion", status: "pending" },
    ],
    nextActions: ["Monitor loading progress", "Confirm departure"],
    lastUpdate: "45 minutes ago",
    driverStatus: "Loading at warehouse",
    driverContactStatus: "Available",
    driverLastSeen: "10 mins ago",
  },
  {
    id: "9",
    taskId: "T009",
    containerNumber: "COSU7777888",
    shippingLine: "COSCO",
    shippingLineCode: "COSCO",
    taskType: "empty-out",
    taskSubtype: "drop",
    status: "empty",
    driver: "吴师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-15T14:00:00",
    lfd: "2024-01-23T16:00:00",
    shipper: "YZA Retail Group",
    shipperContact: { phone: "555-345-6789", email: "yza@retail.com" },
    warehouse: "VWX Empty Depot",
    warehouseAddress: "4680 Container St, Long Beach, CA",
    warehouseContact: { phone: "555-876-5432", email: "vwx@empty.com" },
    warehouseAppointment: "2024-01-16T10:00:00",
    internalNote: "Standard empty return. Check container condition report.",
    containerSize: "20GP",
    containerType: "dry",
    chassisNumber: "CH-55555",
    importExport: "export",
    originPort: "Los Angeles, CA",
    destinationPort: "Oakland, CA",
    bookingReference: "BK334455",
    vesselInformation: "COSCO PACIFIC / Voyage 009",
    specialInstructions: "Check container condition",
    holdReasons: [],
    portContacts: {
      ets: { ref: "ETS901234", phone: "562-345-6789", email: "ets@port.com" },
    },
    gateCode: "COSCOEMPTY",
    pin: "9876",
    ssaBooking: "COSCOEMPTYBOOK",
    sealNumber: "SL990011",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "Empty Depot",
    availableTimes: "Anytime",
    dispatcherToDriverInstructions: "Submit empty return receipt.",
    processingTimeline: [
      { time: "08:00 AM", description: "Container picked up", status: "completed" },
      { time: "09:00 AM", description: "En route to empty depot", status: "completed" },
      { time: "10:00 AM", description: "Container dropped off at depot", status: "current" },
    ],
    nextActions: ["Confirm empty return receipt"],
    lastUpdate: "1 hour ago",
    driverStatus: "At empty depot",
    driverContactStatus: "Available",
    driverLastSeen: "15 mins ago",
  },
  {
    id: "10",
    taskId: "T010",
    containerNumber: "OOCL9999000",
    shippingLine: "OOCL",
    shippingLineCode: "OOCL",
    taskType: "load-out",
    taskSubtype: "live-load",
    status: "returning",
    driver: "郑师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-14T09:30:00",
    lfd: "2024-01-24T17:00:00",
    shipper: "BCD Electronics",
    shipperContact: { phone: "555-456-1234", email: "bcd@electronics.com" },
    warehouse: "YZA Tech Center",
    warehouseAddress: "9753 Technology Blvd, Irvine, CA",
    warehouseContact: { phone: "555-765-4321", email: "yza@tech.com" },
    warehouseAppointment: "2024-01-15T13:30:00",
    internalNote: "High-value electronics. Security escort required during transport.",
    containerSize: "40GP",
    containerType: "dry",
    chassisNumber: "CH-66666",
    importExport: "export",
    originPort: "Long Beach, CA",
    destinationPort: "Seattle, WA",
    bookingReference: "BK667788",
    vesselInformation: "OOCL HONG KONG / Voyage 010",
    specialInstructions: "Security escort required",
    holdReasons: [],
    portContacts: {
      ssa: { ref: "SSA012345", phone: "562-123-4567", email: "ssa@port.com" },
    },
    gateCode: "OOCLSECURE",
    pin: "5432",
    ssaBooking: "OOCLSECUREBOOK",
    sealNumber: "SL223344",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "En Route to Port",
    availableTimes: "9AM-5PM",
    dispatcherToDriverInstructions: "Follow security protocol.",
    processingTimeline: [
      { time: "12:00 PM", description: "Delivery completed", status: "completed" },
      { time: "01:00 PM", description: "En route to port for return", status: "current" },
      { time: "02:00 PM", description: "Estimated arrival at port", status: "pending" },
    ],
    nextActions: ["Confirm container return at port"],
    lastUpdate: "20 minutes ago",
    driverStatus: "Returning to port",
    driverContactStatus: "Available",
    driverLastSeen: "5 mins ago",
  },
  {
    id: "11",
    taskId: "T011",
    containerNumber: "HLXU1234567",
    shippingLine: "Hapag-Lloyd",
    shippingLineCode: "HPL",
    taskType: "load-in",
    taskSubtype: "drop",
    status: "pause",
    driver: "钱师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-18T11:00:00",
    lfd: "2024-01-26T17:00:00",
    shipper: "EFG Furniture Co",
    shipperContact: { phone: "555-567-8901", email: "efg@furniture.com" },
    warehouse: "BCD Furniture Hub",
    warehouseAddress: "1122 Furniture Row, Gardena, CA",
    warehouseContact: { phone: "555-890-1234", email: "bcd@furniture.com" },
    warehouseAppointment: "2024-01-19T08:00:00",
    internalNote: "Truck breakdown. Waiting for roadside assistance.",
    containerSize: "40HC",
    containerType: "dry",
    chassisNumber: "CH-77777",
    importExport: "n/a",
    originPort: "Los Angeles, CA",
    destinationPort: "Los Angeles, CA",
    bookingReference: "BK990011",
    vesselInformation: "HAPAG LLOYD / Voyage 011",
    specialInstructions: "Fragile items - handle with care",
    holdReasons: ["Truck Breakdown"],
    pauseReasonDetail: "Engine trouble - roadside assistance dispatched",
    etaResume: "2024-01-18T15:00:00",
    portContacts: {
      trapac: { ref: "TRP345678", phone: "562-234-5678", email: "trapac@port.com" },
    },
    gateCode: "HAPAGFURN",
    pin: "6543",
    ssaBooking: "HPLFURNBOOK",
    sealNumber: "SL556677",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "Highway 110 (Breakdown)",
    availableTimes: "8AM-12PM",
    dispatcherToDriverInstructions: "Wait for roadside assistance.",
    processingTimeline: [
      { time: "10:00 AM", description: "Departed port", status: "completed" },
      { time: "11:30 AM", description: "Truck breakdown occurred", status: "current" },
      { time: "15:00 PM", description: "Estimated repair completion", status: "pending" },
    ],
    nextActions: ["Coordinate roadside assistance", "Update ETA with warehouse"],
    lastUpdate: "30 minutes ago",
    driverStatus: "Truck breakdown",
    driverContactStatus: "Available",
    driverLastSeen: "10 mins ago",
  },
  {
    id: "12",
    taskId: "T012",
    containerNumber: "CMAU6543210",
    shippingLine: "CMA CGM",
    shippingLineCode: "CMA",
    taskType: "load-in",
    taskSubtype: "drop",
    status: "released",
    driver: "冯师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-19T15:30:00",
    lfd: "2024-01-27T18:00:00",
    shipper: "HIJ Pharmaceuticals",
    shipperContact: { phone: "555-678-9012", email: "hij@pharma.com" },
    warehouse: "EFG Medical Storage",
    warehouseAddress: "3344 Medical Center Dr, Anaheim, CA",
    warehouseContact: { phone: "555-901-2345", email: "efg@medical.com" },
    warehouseAppointment: "2024-01-20T12:00:00",
    internalNote: "Pharmaceutical products - temperature and humidity control required.",
    containerSize: "20GP",
    containerType: "reefer",
    chassisNumber: "CH-88888",
    importExport: "import",
    originPort: "Hamburg, Germany",
    destinationPort: "Long Beach, CA",
    bookingReference: "BK223344",
    vesselInformation: "CMA CGM / Voyage 012",
    specialInstructions: "Temperature control required",
    holdReasons: [],
    portContacts: {
      ets: { ref: "ETS012345", phone: "562-345-6789", email: "ets@port.com" },
    },
    gateCode: "CMAPHARM",
    pin: "7654",
    ssaBooking: "CMAPHARMBOOK",
    sealNumber: "SL889900",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "Terminal 2",
    availableTimes: "10AM-2PM",
    dispatcherToDriverInstructions: "Maintain strict temperature logs.",
    processingTimeline: [
      { time: "14:00 PM", description: "Container released by port", status: "completed" },
      { time: "15:30 PM", description: "Port appointment scheduled", status: "current" },
      { time: "16:00 PM", description: "Estimated pickup", status: "pending" },
    ],
    nextActions: ["Confirm driver assignment", "Prepare pickup documentation"],
    lastUpdate: "2 hours ago",
    driverStatus: "Ready for pickup",
    driverContactStatus: "Available", 
    driverLastSeen: "30 mins ago",
  },
  {
    id: "13",
    taskId: "T013",
    containerNumber: "MSKU1239876",
    shippingLine: "Maersk",
    shippingLineCode: "MSK",
    taskType: "load-out",
    taskSubtype: "drop",
    status: "vessel",
    driver: "张师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-22T08:00:00",
    lfd: "2024-01-19T10:00:00",
    shipper: "Global Imports Inc.",
    shipperContact: { phone: "555-789-0123", email: "global@imports.com" },
    warehouse: "Port of LA",
    warehouseAddress: "1000 Port Blvd, Los Angeles, CA",
    warehouseContact: { phone: "555-012-3456", email: "portla@info.com" },
    warehouseAppointment: "2024-01-23T09:00:00",
    internalNote: "Container currently on vessel, awaiting arrival at port.",
    containerSize: "40HC",
    containerType: "dry",
    chassisNumber: "CH-99999",
    importExport: "import",
    originPort: "Singapore",
    destinationPort: "Los Angeles, CA",
    bookingReference: "BK556677",
    vesselInformation: "MAERSK EINSTEIN / Voyage 013",
    specialInstructions: "ETA Jan 22",
    holdReasons: [],
    portContacts: {
      ssa: { ref: "SSA345678", phone: "562-123-4567", email: "ssa@port.com" },
    },
    gateCode: "MAERSKLA",
    pin: "1029",
    ssaBooking: "MSKLAXBOOK",
    sealNumber: "SL123123",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "At Sea",
    availableTimes: "N/A",
    dispatcherToDriverInstructions: "Prepare for port pickup.",
    processingTimeline: [
      { time: "Jan 15", description: "Vessel departed origin", status: "completed" },
      { time: "Jan 19", description: "Vessel en route", status: "current" },
      { time: "Jan 22", description: "Estimated vessel arrival", status: "pending" },
    ],
    nextActions: ["Monitor vessel ETA", "Prepare port appointment"],
    lastUpdate: "6 hours ago",
    driverStatus: "N/A (on vessel)",
    driverContactStatus: "N/A",
    driverLastSeen: "N/A",
  },
  {
    id: "14",
    taskId: "T014",
    containerNumber: "COSCO4567890",
    shippingLine: "COSCO",
    shippingLineCode: "COSCO",
    taskType: "empty-in",
    taskSubtype: "live-load",
    status: "picking",
    driver: "李师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-19T14:00:00",
    lfd: "2024-01-20T16:00:00",
    shipper: "Export Goods Co.",
    shipperContact: { phone: "555-890-1234", email: "export@goods.com" },
    warehouse: "Port of Long Beach",
    warehouseAddress: "2000 Port Way, Long Beach, CA",
    warehouseContact: { phone: "555-234-5678", email: "portlb@info.com" },
    warehouseAppointment: "2024-01-20T10:00:00",
    internalNote: "High priority export. Time-sensitive pickup required.",
    containerSize: "20GP",
    containerType: "dry",
    chassisNumber: "CH-00000",
    importExport: "export",
    originPort: "Long Beach, CA",
    destinationPort: "Shanghai, China",
    bookingReference: "BK889900",
    vesselInformation: "COSCO SHIPPING UNIVERSE / Voyage 014",
    specialInstructions: "Priority pickup - time sensitive",
    holdReasons: [],
    portContacts: {
      trapac: { ref: "TRP678901", phone: "562-234-5678", email: "trapac@port.com" },
    },
    gateCode: "COSCOLB",
    pin: "3049",
    ssaBooking: "COSCOLBBOOK",
    sealNumber: "SL456456",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "Terminal 1",
    availableTimes: "8AM-6PM",
    dispatcherToDriverInstructions: "Expedite pickup - priority export.",
    processingTimeline: [
      { time: "13:00 PM", description: "Driver dispatched", status: "completed" },
      { time: "14:00 PM", description: "Arrived at terminal", status: "current" },
      { time: "14:30 PM", description: "Estimated pickup completion", status: "pending" },
    ],
    nextActions: ["Expedite terminal pickup", "Prepare for vessel loading"],
    lastUpdate: "15 minutes ago",
    driverStatus: "At terminal, picking up",
    driverContactStatus: "Available",
    driverLastSeen: "2 mins ago",
  },
  {
    id: "15",
    taskId: "T015",
    containerNumber: "ONEU1234567",
    shippingLine: "ONE",
    shippingLineCode: "ONE",
    taskType: "load-in",
    taskSubtype: "drop",
    status: "on-hold",
    driver: "赵师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-19T09:00:00",
    lfd: "2024-01-18T12:00:00",
    shipper: "Import Goods Inc.",
    shipperContact: { phone: "555-901-2345", email: "import@goods.com" },
    warehouse: "Warehouse A",
    warehouseAddress: "100 Warehouse Rd, Los Angeles, CA",
    warehouseContact: { phone: "555-345-6789", email: "warehousea@info.com" },
    warehouseAppointment: "2024-01-20T10:00:00",
    internalNote: "Delayed due to documentation issues. Customs review required.",
    containerSize: "40GP",
    containerType: "dry",
    chassisNumber: "CH-12346",
    importExport: "import",
    originPort: "Tokyo, Japan",
    destinationPort: "Los Angeles, CA",
    bookingReference: "BK112234",
    vesselInformation: "ONE AQUARIUS / Voyage 015",
    specialInstructions: "Customs review required",
    holdReasons: ["Documentation Issues", "Customs Review"],
    holdReasonDetail: "Import permit documentation incomplete",
    portContacts: {
      ets: { ref: "ETS456789", phone: "562-345-6789", email: "ets@port.com" },
    },
    gateCode: "ONEWAREHOUSE",
    pin: "5678",
    ssaBooking: "ONEBOOK",
    sealNumber: "SL789789",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "Customs Hold",
    availableTimes: "N/A",
    dispatcherToDriverInstructions: "Wait for customs clearance.",
    processingTimeline: [
      { time: "08:00 AM", description: "Container arrived", status: "completed" },
      { time: "09:00 AM", description: "Customs hold initiated", status: "current" },
      { time: "TBD", description: "Customs clearance", status: "pending" },
    ],
    nextActions: ["Follow up with customs broker", "Update documentation"],
    lastUpdate: "4 hours ago",
    driverStatus: "Waiting for clearance",
    driverContactStatus: "Available",
    driverLastSeen: "2 hours ago",
  },
  {
    id: "16",
    taskId: "T016",
    containerNumber: "EGHU2468135",
    shippingLine: "Evergreen",
    shippingLineCode: "EMC",
    taskType: "load-out",
    taskSubtype: "live-load",
    status: "delivering",
    driver: "孙师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-18T10:00:00",
    lfd: "2024-01-25T16:00:00",
    shipper: "Tech Components Inc",
    shipperContact: { phone: "555-111-3333", email: "tech@components.com" },
    warehouse: "Silicon Valley Distribution",
    warehouseAddress: "5555 Tech Drive, San Jose, CA",
    warehouseContact: { phone: "555-222-4444", email: "sv@distribution.com" },
    warehouseAppointment: "2024-01-19T14:00:00",
    internalNote: "Electronic components - anti-static handling required.",
    containerSize: "40GP",
    containerType: "dry",
    chassisNumber: "CH-13579",
    importExport: "export",
    originPort: "Oakland, CA",
    destinationPort: "Hamburg, Germany",
    bookingReference: "BK135792",
    vesselInformation: "EVERGREEN STAR / Voyage 016",
    specialInstructions: "Anti-static handling required",
    holdReasons: [],
    portContacts: {
      ssa: { ref: "SSA135792", phone: "562-123-4567", email: "ssa@port.com" },
    },
    gateCode: "EVERGREENTECH",
    pin: "1357",
    ssaBooking: "EMCTECHBOOK",
    sealNumber: "SL135792",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "Highway 101 North",
    availableTimes: "10AM-6PM",
    dispatcherToDriverInstructions: "Use anti-static procedures.",
    processingTimeline: [
      { time: "08:00 AM", description: "Departed port", status: "completed" },
      { time: "12:00 PM", description: "En route to San Jose", status: "current" },
      { time: "14:00 PM", description: "Estimated arrival", status: "pending" },
    ],
    nextActions: ["Monitor delivery progress", "Confirm receipt"],
    lastUpdate: "1 hour ago",
    driverStatus: "En route to delivery",
    driverContactStatus: "Available",
    driverLastSeen: "20 mins ago",
  },
  {
    id: "17",
    taskId: "T017",
    containerNumber: "HLXU9876543",
    shippingLine: "Hapag-Lloyd",
    shippingLineCode: "HPL",
    taskType: "empty-out",
    taskSubtype: "drop",
    status: "completed",
    driver: "周师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-15T08:00:00",
    lfd: "2024-01-17T17:00:00",
    shipper: "Automotive Parts Co",
    shipperContact: { phone: "555-333-5555", email: "auto@parts.com" },
    warehouse: "Auto Parts Depot",
    warehouseAddress: "7777 Auto Blvd, Detroit, MI",
    warehouseContact: { phone: "555-444-6666", email: "depot@autoparts.com" },
    warehouseAppointment: "2024-01-16T11:00:00",
    internalNote: "Empty return completed successfully. No damage reported.",
    containerSize: "20GP",
    containerType: "dry",
    chassisNumber: "CH-24680",
    importExport: "n/a",
    originPort: "Los Angeles, CA",
    destinationPort: "Los Angeles, CA",
    bookingReference: "BK246810",
    vesselInformation: "HAPAG MUNICH / Voyage 017",
    specialInstructions: "Standard empty return",
    holdReasons: [],
    portContacts: {
      trapac: { ref: "TRP246810", phone: "562-234-5678", email: "trapac@port.com" },
    },
    gateCode: "HAPAGEMPTY",
    pin: "2468",
    ssaBooking: "HPLEMPTYBOOK",
    sealNumber: "SL246810",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "Empty Depot",
    availableTimes: "Completed",
    dispatcherToDriverInstructions: "Task completed successfully.",
    processingTimeline: [
      { time: "07:00 AM", description: "Departed for pickup", status: "completed" },
      { time: "08:30 AM", description: "Container picked up", status: "completed" },
      { time: "10:00 AM", description: "Returned to depot", status: "completed" },
    ],
    nextActions: ["Archive completed task"],
    lastUpdate: "3 days ago",
    driverStatus: "Task completed",
    driverContactStatus: "Available",
    driverLastSeen: "3 days ago",
  },
  {
    id: "18",
    taskId: "T018",
    containerNumber: "CMAU1357924",
    shippingLine: "CMA CGM",
    shippingLineCode: "CMA",
    taskType: "load-in",
    taskSubtype: "live-load",
    status: "loading",
    driver: "吴师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-19T13:00:00",
    lfd: "2024-01-28T18:00:00",
    shipper: "Fashion Imports LLC",
    shipperContact: { phone: "555-555-7777", email: "fashion@imports.com" },
    warehouse: "Fashion Distribution Center",
    warehouseAddress: "8888 Fashion Ave, Los Angeles, CA",
    warehouseContact: { phone: "555-666-8888", email: "fdc@fashion.com" },
    warehouseAppointment: "2024-01-19T15:00:00",
    internalNote: "High-value fashion items. Loading in progress with extra security.",
    containerSize: "40HC",
    containerType: "dry",
    chassisNumber: "CH-97531",
    importExport: "import",
    originPort: "Le Havre, France",
    destinationPort: "Long Beach, CA",
    bookingReference: "BK975310",
    vesselInformation: "CMA CGM MARCO POLO / Voyage 018",
    specialInstructions: "High-value cargo - security required",
    holdReasons: [],
    portContacts: {
      ets: { ref: "ETS975310", phone: "562-345-6789", email: "ets@port.com" },
    },
    gateCode: "CMAFASHION",
    pin: "9753",
    ssaBooking: "CMAFASHIONBOOK",
    sealNumber: "SL975310",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "Warehouse Loading Dock",
    availableTimes: "1PM-7PM",
    dispatcherToDriverInstructions: "Monitor valuable cargo loading.",
    processingTimeline: [
      { time: "12:00 PM", description: "Arrived at warehouse", status: "completed" },
      { time: "13:00 PM", description: "Loading started", status: "current" },
      { time: "15:00 PM", description: "Estimated loading completion", status: "pending" },
    ],
    nextActions: ["Monitor loading completion", "Verify cargo manifest"],
    lastUpdate: "2 hours ago",
    driverStatus: "Supervising loading",
    driverContactStatus: "Available",
    driverLastSeen: "30 mins ago",
  },
  {
    id: "19",
    taskId: "T019",
    containerNumber: "YNIU8642097",
    shippingLine: "Yang Ming",
    shippingLineCode: "YML",
    taskType: "empty-in",
    taskSubtype: "drop",
    status: "returning",
    driver: "郑师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-18T16:00:00",
    lfd: "2024-01-24T19:00:00",
    shipper: "Marine Equipment Co",
    shipperContact: { phone: "555-777-9999", email: "marine@equipment.com" },
    warehouse: "Marine Storage Facility",
    warehouseAddress: "9999 Harbor Dr, San Pedro, CA",
    warehouseContact: { phone: "555-888-0000", email: "marine@storage.com" },
    warehouseAppointment: "2024-01-19T10:00:00",
    internalNote: "Marine equipment delivered. Returning empty container to port.",
    containerSize: "45HC",
    containerType: "dry",
    chassisNumber: "CH-86420",
    importExport: "import",
    originPort: "Kaohsiung, Taiwan",
    destinationPort: "Los Angeles, CA",
    bookingReference: "BK864209",
    vesselInformation: "YANG MING WISDOM / Voyage 019",
    specialInstructions: "Standard empty return",
    holdReasons: [],
    portContacts: {
      ssa: { ref: "SSA864209", phone: "562-123-4567", email: "ssa@port.com" },
    },
    gateCode: "YANGMINGMARINE",
    pin: "8642",
    ssaBooking: "YMLMARINEBOOK",
    sealNumber: "SL864209",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "En Route to Port",
    availableTimes: "8AM-8PM",
    dispatcherToDriverInstructions: "Return empty to Yang Ming terminal.",
    processingTimeline: [
      { time: "08:00 AM", description: "Delivered cargo", status: "completed" },
      { time: "09:00 AM", description: "En route to port", status: "current" },
      { time: "10:30 AM", description: "Estimated port arrival", status: "pending" },
    ],
    nextActions: ["Confirm empty return at port"],
    lastUpdate: "45 minutes ago",
    driverStatus: "Returning empty to port",
    driverContactStatus: "Available",
    driverLastSeen: "15 mins ago",
  },
  {
    id: "20",
    taskId: "T020",
    containerNumber: "OOCL5297431",
    shippingLine: "OOCL",
    shippingLineCode: "OOCL",
    taskType: "load-out",
    taskSubtype: "drop",
    status: "released",
    driver: "钱师傅",
    driverAvatar: "/placeholder.svg?height=32&width=32",
    portAppointment: "2024-01-20T11:00:00",
    lfd: "2024-01-29T17:00:00",
    shipper: "Agricultural Products Inc",
    shipperContact: { phone: "555-999-1111", email: "agri@products.com" },
    warehouse: "Fresh Produce Terminal",
    warehouseAddress: "1111 Fresh Market Rd, Salinas, CA",
    warehouseContact: { phone: "555-000-2222", email: "fresh@terminal.com" },
    warehouseAppointment: "2024-01-21T06:00:00",
    internalNote: "Fresh produce export. Time-sensitive perishable goods.",
    containerSize: "40RF",
    containerType: "reefer",
    chassisNumber: "CH-52974",
    importExport: "export",
    originPort: "Oakland, CA",
    destinationPort: "Tokyo, Japan",
    bookingReference: "BK529743",
    vesselInformation: "OOCL TOKYO / Voyage 020",
    specialInstructions: "Perishable goods - maintain cold chain",
    holdReasons: [],
    portContacts: {
      ssa: { ref: "SSA529743", phone: "562-123-4567", email: "ssa@port.com" },
    },
    gateCode: "OOCLFRESH",
    pin: "5297",
    ssaBooking: "OOCLFRESHBOOK",
    sealNumber: "SL529743",
    oaDocumentUrl: "/placeholder.svg?height=24&width=24",
    boDocumentUrl: "/placeholder.svg?height=24&width=24",
    currentLocation: "Terminal 7",
    availableTimes: "5AM-11AM",
    dispatcherToDriverInstructions: "Priority perishable - maintain temperature.",
    processingTimeline: [
      { time: "09:00 AM", description: "Container released", status: "completed" },
      { time: "11:00 AM", description: "Port appointment scheduled", status: "current" },
      { time: "11:30 AM", description: "Estimated pickup", status: "pending" },
    ],
    nextActions: ["Assign reefer-certified driver", "Monitor temperature"],
    lastUpdate: "3 hours ago",
    driverStatus: "Ready for reefer pickup",
    driverContactStatus: "Available",
    driverLastSeen: "1 hour ago",
  },
  // Off container - single container that can be assigned to multiple drivers
  {
    id: "off-container",
    taskId: "OFF",
    containerNumber: "OFF",
    shippingLine: "OFF",
    shippingLineCode: "OFF",
    taskType: "load-out",
    taskSubtype: "live-load",
    status: "off",
    driver: "",
    driverAvatar: "",
    portAppointment: "2024-01-18T00:00:00", // Tomorrow's date
    lfd: "2024-01-18T00:00:00",
    shipper: "OFF",
    shipperContact: { phone: "", email: "" },
    warehouse: "OFF",
    warehouseAddress: "OFF",
    warehouseContact: { phone: "", email: "" },
    warehouseAppointment: "2024-01-18T00:00:00",
    internalNote: "Driver will be OFF tomorrow",
    containerSize: "OFF",
    containerType: "dry",
    chassisNumber: "OFF",
    weight: 0,
    importExport: "n/a",
    originPort: "OFF",
    destinationPort: "OFF",
    bookingReference: "OFF",
    vesselInformation: "OFF",
    specialInstructions: "Driver off duty tomorrow",
    holdReasons: [],
    portContacts: {},
    gateCode: "OFF",
    pin: "OFF",
    ssaBooking: "OFF",
    sealNumber: "OFF",
    oaDocumentUrl: "",
    boDocumentUrl: "",
    currentLocation: "OFF",
    availableTimes: "OFF",
    dispatcherToDriverInstructions: "Driver will be off tomorrow",
    processingTimeline: [],
    nextActions: [],
    lastUpdate: "Always available",
    driverStatus: "Off duty tomorrow",
    driverContactStatus: "Off",
    driverLastSeen: "N/A",
  },
  // Standby container - single container that can be assigned to multiple drivers
  {
    id: "standby-container",
    taskId: "0000",
    containerNumber: "STANDBY",
    shippingLine: "STANDBY",
    shippingLineCode: "STANDBY",
    taskType: "load-out",
    taskSubtype: "live-load",
    status: "standby",
    driver: "",
    driverAvatar: "",
    portAppointment: "2024-01-18T00:00:00", // Today's date
    lfd: "2024-01-18T00:00:00",
    shipper: "STANDBY",
    shipperContact: { phone: "", email: "" },
    warehouse: "STANDBY",
    warehouseAddress: "STANDBY",
    warehouseContact: { phone: "", email: "" },
    warehouseAppointment: "2024-01-18T00:00:00",
    internalNote: "Driver on standby duty",
    containerSize: "STANDBY",
    containerType: "dry",
    chassisNumber: "STANDBY",
    weight: 0,
    importExport: "n/a",
    originPort: "STANDBY",
    destinationPort: "STANDBY",
    bookingReference: "STANDBY",
    vesselInformation: "STANDBY",
    specialInstructions: "Driver on standby duty",
    holdReasons: [],
    portContacts: {},
    gateCode: "STANDBY",
    pin: "STANDBY",
    ssaBooking: "STANDBY",
    sealNumber: "STANDBY",
    oaDocumentUrl: "",
    boDocumentUrl: "",
    currentLocation: "STANDBY",
    availableTimes: "STANDBY",
    dispatcherToDriverInstructions: "Driver on standby duty",
    processingTimeline: [],
    nextActions: [],
    lastUpdate: "Always available",
    driverStatus: "On standby duty",
    driverContactStatus: "Standby",
    driverLastSeen: "N/A",
  },
]